import axios from 'axios';
import { 
  API_BASE_URL, 
  API_CONFIG, 
  getTokenFromStorage,
  STORAGE_KEYS
} from '../constant/constant';

// API and Billing Service Endpoints
export const API_AND_BILLING_ENDPOINTS = {
  DASHBOARD_METRICS: '/admin/dashboard/metrics',
  DASHBOARD_TRANSACTIONS: '/admin/dashboard/transactions',
  DASHBOARD_REVENUE_TRENDS: '/admin/dashboard/revenue-trends',
  DASHBOARD_PRICING_PLANS: '/admin/dashboard/pricing-plans',
  DASHBOARD_TOP_API_USERS: '/admin/dashboard/top-api-users',
  // Pricing Plan Management
  PRICING_PLANS: '/admin/pricing-plans',
  PRICING_PLAN_DETAILS: (productId) => `/admin/pricing-plans/${productId}`,
  // Stripe Integration
  STRIPE_PRODUCTS: '/admin/stripe/products',
  STRIPE_PRICES: '/admin/stripe/prices'
};

// Create axios instance with default config
const createApiAndBillingApi = () => {
  const apiAndBillingApi = axios.create({
    baseURL: API_BASE_URL,
  });

  // Request interceptor to add auth token
  apiAndBillingApi.interceptors.request.use(
    (config) => {
      const token = getTokenFromStorage();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  apiAndBillingApi.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Token expired or invalid, clear Redux Persist storage and redirect to login
        localStorage.removeItem(STORAGE_KEYS.PERSIST_ROOT);
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return apiAndBillingApi;
};

const apiAndBillingApi = createApiAndBillingApi();

// Helper function to handle API errors
const handleApiError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.msg || error.message || defaultMessage;
  throw new Error(errorMessage);
};

// Helper function to build query parameters
const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value.toString());
    }
  });
  
  return queryParams.toString();
};

// API and Billing Service Functions

// Get dashboard key metrics
export const getDashboardMetrics = async () => {
  try {
    const response = await apiAndBillingApi.get(API_AND_BILLING_ENDPOINTS.DASHBOARD_METRICS);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch dashboard metrics');
  }
};

// Get recent transactions with pagination and filtering
export const getRecentTransactions = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = API_CONFIG.DEFAULT_PAGE_SIZE,
      status = 'all',
      plan = 'all'
    } = params;

    const queryParams = buildQueryParams({
      page,
      limit,
      status,
      plan
    });

    const response = await apiAndBillingApi.get(`${API_AND_BILLING_ENDPOINTS.DASHBOARD_TRANSACTIONS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch recent transactions');
  }
};

// Get revenue trends
export const getRevenueTrends = async (months = 7) => {
  try {
    const queryParams = buildQueryParams({ months });
    const response = await apiAndBillingApi.get(`${API_AND_BILLING_ENDPOINTS.DASHBOARD_REVENUE_TRENDS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch revenue trends');
  }
};

// Get pricing plans overview
export const getPricingPlansOverview = async () => {
  try {
    const response = await apiAndBillingApi.get(API_AND_BILLING_ENDPOINTS.DASHBOARD_PRICING_PLANS);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch pricing plans overview');
  }
};

// Get top API users
export const getTopApiUsers = async (limit = 6) => {
  try {
    const queryParams = buildQueryParams({ limit });
    const response = await apiAndBillingApi.get(`${API_AND_BILLING_ENDPOINTS.DASHBOARD_TOP_API_USERS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch top API users');
  }
};

// Pricing Plan Management Functions

// Create new pricing plan
export const createPricingPlan = async (planData) => {
  try {
    const response = await apiAndBillingApi.post(API_AND_BILLING_ENDPOINTS.PRICING_PLANS, planData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create pricing plan');
  }
};

// Update pricing plan
export const updatePricingPlan = async (productId, updateData) => {
  try {
    const response = await apiAndBillingApi.put(API_AND_BILLING_ENDPOINTS.PRICING_PLAN_DETAILS(productId), updateData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update pricing plan');
  }
};

// Deactivate pricing plan
export const deactivatePricingPlan = async (productId, deactivateData = {}) => {
  try {
    const response = await apiAndBillingApi.delete(API_AND_BILLING_ENDPOINTS.PRICING_PLAN_DETAILS(productId), {
      data: deactivateData
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to deactivate pricing plan');
  }
};

// Get pricing plan details
export const getPricingPlanDetails = async (productId) => {
  try {
    const response = await apiAndBillingApi.get(API_AND_BILLING_ENDPOINTS.PRICING_PLAN_DETAILS(productId));
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch pricing plan details');
  }
};

// Stripe Integration Functions

// Get all Stripe products
export const getStripeProducts = async (params = {}) => {
  try {
    const {
      limit = 100,
      active = null
    } = params;

    const queryParams = buildQueryParams({
      limit,
      ...(active !== null && { active: active.toString() })
    });

    const response = await apiAndBillingApi.get(`${API_AND_BILLING_ENDPOINTS.STRIPE_PRODUCTS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch Stripe products');
  }
};

// Get all Stripe prices
export const getStripePrices = async (params = {}) => {
  try {
    const {
      limit = 100,
      active = null,
      product = null
    } = params;

    const queryParams = buildQueryParams({
      limit,
      ...(active !== null && { active: active.toString() }),
      ...(product && { product })
    });

    const response = await apiAndBillingApi.get(`${API_AND_BILLING_ENDPOINTS.STRIPE_PRICES}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch Stripe prices');
  }
};

// Export all functions as apiAndBillingService object for backward compatibility
export const apiAndBillingService = {
  // Dashboard functions
  getDashboardMetrics,
  getRecentTransactions,
  getRevenueTrends,
  getPricingPlansOverview,
  getTopApiUsers,
  // Pricing Plan Management
  createPricingPlan,
  updatePricingPlan,
  deactivatePricingPlan,
  getPricingPlanDetails,
  // Stripe Integration
  getStripeProducts,
  getStripePrices
};

export default apiAndBillingService;
